import React, {useCallback, useMemo, useState} from "react";
import {AutoComplete, Button, ButtonToolbar, Form, Modal, SelectPicker} from 'rsuite';
import {addNewUserSkill} from "../../../service/skillService";
import {skillModel} from "../../form/models";

const AddSkillModal = (props) => {
    const { open, handleClose, skills, skillTypes, skillExpLevels, userId, unformattedSkills } = props;

    // default to N/A and 0 YoE - this indicates a user has not updated their information
    const [localState, setLocalState] = useState({userId, skillExpLevel: 'N/A', yearsExp: 0});

    const localClose = useCallback(() => {
        setLocalState({userId, skillName: '', skillType: '', skillExpLevel: 'N/A', yearsExp: 0});
        handleClose({closeModal: 'true'});
    }, [handleClose, userId]);

    // buttons below are given a class name
    // we will key off of addReturn/addContinue to decide whether or not to close the modal
    const submitSkillAdd = useCallback((isFormValid, event) => {
        if(!isFormValid) return;
        addNewUserSkill(localState).then((response) => {
            response['closeModal'] = /addReturn/.test(event.nativeEvent.submitter.className);
            handleClose(response);
            setLocalState({userId, skillName: '', skillType: '', skillExpLevel: 'N/A', yearsExp: 0});
        });
    }, [handleClose, localState, userId]);

    // rsuite does not have an onSubmit, we must handle state changes per controlled field
    // When a skill is typed/selected, check to see if this already exists and set the type automatically.
    // The user could 'X' is out and then form validation would fire and be confusing, but we'll cross that
    // bridge when we get there.
    // For now this is fairly simple and elegant to force the type instead of letting people choose and the backend
    // overwriting it anyway.
    const handleFormChange = useCallback((event) => {
        if (event.skillName !== undefined) {
            const associatedSkillType = unformattedSkills.find((skill) => skill.skillName === event.skillName);
            if (associatedSkillType !== undefined) {
                console.debug(`Found associated skill type: ${JSON.stringify(associatedSkillType)}`);
                setLocalState({...localState, ...event, skillType: associatedSkillType.skillType});
            }
            else {
                setLocalState({...localState, ...event});
            }
        } else {
            setLocalState({...localState, ...event});
        }
    }, [localState, unformattedSkills]);

    // In order to have the above type setting fire correctly, we set the formValue to be localState.
    // TODO investigate the uncontrolled -> controlled message. There's an errant undefined somewhere
    return useMemo(() => {
        return (
            <Modal
                open={open}
                onClose={() => localClose()}
                backdrop="static"
            >
                <Modal.Header>
                    <Modal.Title>Add Skill</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Add a skill you have: specify name, type, experience level, and years of experience.
                    </p>
                    <p>
                        If you need a new skill type added, please contact an administrator.
                    </p>
                    <p>
                        Skill names are unique.
                    </p>
                </Modal.Body>
                <Form
                    layout="horizontal"
                    model={skillModel}
                    onChange={handleFormChange}
                    onSubmit={submitSkillAdd}
                    formValue={localState}
                >
                    <Form.Group>
                        <Form.ControlLabel>Skill Name</Form.ControlLabel>
                        <Form.Control name="skillName"
                                      accepter={AutoComplete}
                                      data={skills}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.ControlLabel>Skill Type</Form.ControlLabel>
                        <Form.Control name="skillType"
                                      accepter={SelectPicker}
                                      data={skillTypes}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.ControlLabel>Experience Level</Form.ControlLabel>
                        <Form.Control name="skillExpLevel"
                                      accepter={SelectPicker}
                                      data={skillExpLevels}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.ControlLabel>Years Experience</Form.ControlLabel>
                        <Form.Control name="yearsExp"/>
                    </Form.Group>
                    <Form.Group>
                        <ButtonToolbar>
                            <Button type="submit" appearance="primary" className="addReturn">
                                Add and Return
                            </Button>
                            <Button type="submit" appearance="primary" className="addContinue">
                                Add and Continue
                            </Button>
                            <Button onClick={() => localClose()} appearance="default">
                                Cancel
                            </Button>
                        </ButtonToolbar>
                    </Form.Group>
                </Form>
            </Modal>
        );
        },
        [
            handleFormChange,
            localClose,
            localState,
            open,
            skillExpLevels,
            skillTypes,
            skills,
            submitSkillAdd]
    );
}

export default AddSkillModal;